// react imports
import React, { useState, useRef, useEffect } from "react";
import { useUser } from "hooks/useUser";
import { useLocation, useHistory } from "react-router-dom";

import { Formik, Form, Field } from "formik";

import { ThemeProvider } from "@mui/material/styles";
import { Typography, Box, Divider, MenuItem, Avatar } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import {
  TextField,
  PasswordField,
  Select,
  FileUploadWrapper,
  Checkbox,
} from "lib";
import { uploadSingleFile } from "utils/functions";

import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import customTheme from "lib/theme";
import {
  UtilityButton,
  NavigationButton,
} from "lib/components/Buttons/buttons.jsx";

import { toast } from "react-toastify";
import * as yup from "yup";
import { emailRegex, phoneRegex } from "utils/regex";
// import axios from "api_darex/axios";
import axios from "api_inbusiness/axios";

// import loginImage from "resources/LoginFormImage.png";
import loginImage from "resources/img/InBusiness/logo.png";
import DefaultUserPicture from "resources/img/InBusiness/defaultUser.png";
import DefaultCompanyPicture from "resources/img/InBusiness/defaultImage.png";

import styles from "./Login.module.scss";

import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

import { CompaniesAPI, CountiesAPI, UsersAPI } from "api_inbusiness";

import inputStyles from "styles/inputStyles.module.scss";

const SUCEAVA_COUNTY_ID = "5a65de97-3228-4bf6-b51b-a3f931790037";

function Login() {
  const { t } = useTranslation();

  const formRef = useRef();
  const recoverFormRef = useRef();
  const signUpFormRef = useRef();

  const [recoverPassword, setRecoverPassword] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);

  const [userFileBlob, setUserFileBlob] = useState(null);
  const [companyFileBlob, setCompanyFileBlob] = useState(null);

  const statusList = [
    {
      id: "guest",
      name: "Invitat",
    },
    {
      id: "activeMember",
      name: "Membru",
    },
    {
      id: "leader",
      name: "Lider Club",
    },
    {
      id: "manager",
      name: "Manager",
    },
  ];

  //TO DO: change this to an empty array
  const [locations, setLocations] = useState([]);

  const { setAccessToken } = useUser();
  const location = useLocation();
  const history = useHistory();
  
  // Get redirect URL from query params
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get("redirect");

  /**
   * Login form
   */
  const INITIAL_FORM_STATE = {
    email: "",
    password: "",
  };

  const FORM_VALIDATION = yup.object().shape({
    email: yup
      .string()
      .trim()
      // .matches(emailRegex, t("You must enter a valid email address!"))
      // .required(t("Email address is mandatory!")),
      .matches(emailRegex, "Trebuie să introduceți un format de email valid!")
      .required("Adresa de email este obligatorie!"),
    password: yup
      .string()
      .trim()
      // .typeError(t("You must enter a valid password format!"))
      // .required(t("Password is required!")),
      .typeError("Trebuie să introduceți un format de parolă valid!")
      .required("Parola este obligatorie!"),
  });

  const handleSubmitFormik = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  const handleLogin = async (values) => {
    try {
      // Clear any existing stale tokens before attempting login
      localStorage.removeItem("accessToken");
      axios.defaults.headers.common.Authorization = undefined;

      const response = await axios.post("/users/login", {
        email: values.email,
        password: values.password,
      });
      const { token } = response.data;
      // toast.success(t("You have successfully logged in!"));
      toast.success("V-ați autentificat cu succes!");
      
      // Save redirect URL to localStorage before setting token (triggers re-render)
      if (redirectUrl) {
        localStorage.setItem("loginRedirect", redirectUrl);
      }
      
      setAccessToken(token);
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          toast.error("Email-ul sau parola sunt greșite!");
        } else if (error.response.status === 403) {
          toast.error(
            "Acces interzis. Verificați dacă aveți permisiunea necesară!"
          );
        } else if (error.response.data?.errors?.User === " not found") {
          toast.error("Email-ul sau parola sunt greșite!");
        } else {
          toast.error("A apărut o eroare la autentificare. Încercați din nou!");
        }
      } else {
        toast.error("Eroare de conexiune. Verificați conexiunea la internet!");
      }
    }
  };

  /**
   * Recover password form
   */
  const INITIAL_FORM_STATE_RECOVER = {
    email: "",
  };

  const FORM_VALIDATION_RECOVER = yup.object().shape({
    email: yup
      .string()
      .trim()
      // .matches(emailRegex, t("You must enter a valid email address!"))
      // .required(t("Email address is mandatory!")),
      .matches(emailRegex, "Trebuie să introduceți un format de email valid!")
      .required("Adresa de email este obligatorie!"),
  });

  const handleSubmitRecoverForm = () => {
    if (recoverFormRef.current) {
      recoverFormRef.current.handleSubmit();
    }
  };

  const handleRecoverPassword = async (values) => {
    UsersAPI.recoverPassword(values.email).then((res) => {
      if (res.ok) {
        setRecoverPassword(false);
        // toast.success(t("You have successfully sent the reset link!"));
        toast.success("Ați trimis cu succes linkul de resetare!");
      } else {
        toast.error(t(res.error.response.data.message));
      }
    });
  };

  /**
   * Sign Up form
   */

  const suceavaLocation = locations.find((l) =>
    l.name?.toLowerCase().includes("suceava")
  );
  const selectedCountyId = suceavaLocation?.id || SUCEAVA_COUNTY_ID;

  const INITIAL_FORM_STATE_SIGNUP = {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    countyId: selectedCountyId,
    companyName: "",
    companyDetails: "",
    // type: "",
    password: "",
    gdpr: false,
  };

  const FORM_VALIDATION_SIGNUP = yup.object().shape({
    firstName: yup
      .string()
      .typeError("Prenumele nu este valid!")
      .trim()
      .min(1, "Prenumele trebuie să conțină cel puțin un caracter!")
      .required("Prenumele este obligatoriu!"),
    lastName: yup
      .string()
      .typeError("Acest nume de familie nu este valabil!")
      .trim()
      .min(1, "Numele de familie trebuie să conțină cel puțin un caracter!")
      .required("Numele de familie este obligatoriu!"),
    phone: yup
      .string()
      .trim()
      .matches(phoneRegex, "Trebuie sa introduceti un numar de telefon valid!")
      .required("Numărul de telefon este obligatoriu!"),
    email: yup
      .string()
      .trim()
      .matches(emailRegex, "Trebuie să introduceți un format de email valid!")
      .required("Adresa de email este obligatorie!"),
    countyId: yup
      .string()
      .trim()
      .required("Locația clubului este obligatorie!"),
    companyName: yup
      .string()
      .typeError("Acest nume de companie nu este valid!")
      .trim()
      .min(3, "Numele companiei trebuie să conțină cel puțin 3 caractere!")
      .required("Numele companiei este obligatoriu!"),
    companyDetails: yup
      .string()
      .typeError("Numele acestui site nu este valid!")
      .trim()
      .min(3, "Numele site-ului trebuie să conțină cel puțin 3 caractere!")
      .required("Numele site-ului este obligatoriu!"),
    // type: yup.string().trim().required("Statusul este obligatoriu!"),
    password: yup
      .string()
      .typeError("Parola nu este validă!")
      .trim()
      .min(1, "Parola trebuie să conțină cel puțin un caracter!")
      .required("Parola este obligatorie!"),
    gdpr: yup
      .boolean()
      .oneOf([true], "Trebuie să fiți de acord cu termenii și condițiile GDPR!"),
  });

  const handleSubmitSignUpForm = () => {
    if (signUpFormRef.current) {
      signUpFormRef.current.handleSubmit();
    }
  };

  const handleSignUp = (values) => {
    const objForSignUp = { ...values };
    objForSignUp.type = "GUEST";

    if (values.gdpr === false) {
      toast.error("Trebuie să fiți de acord cu politica GDPR!");
    } else {
      delete objForSignUp.gdpr;
      UsersAPI.create(objForSignUp).then((res) => {
        if (res.ok === true) {
          if (userFileBlob) {
            UsersAPI.addPicture(res.data.id, userFileBlob).then((res) => {
              if (res.ok === true) {
                toast.success(t("Poza de profil a fost adăugată cu succes"));
              } else {
                toast.error(
                  t(
                    "A intervenit o eroare! Poza de profil nu a putut fi încărcată!"
                  )
                );
              }
            });
          }
          if (companyFileBlob) {
            CompaniesAPI.addPicture(res.data.id, companyFileBlob).then(
              (res) => {
                if (res.ok === true) {
                  toast.success("Logo-ul companiei a fost adăugat cu succes!");
                } else {
                  toast.error(
                    "A intervenit o eroare la adăugarea logo-ului companiei!"
                  );
                }
              }
            );
          }

          setUserFileBlob(null);
          setCompanyFileBlob(null);
          setShowSignUp(false);
          toast.success(t("Utilizator creat cu succes!"));
        } else {
          const errorMsg =
            res.error?.response?.data?.errors?.User ||
            res.error?.response?.data?.errors?.message ||
            res.error?.response?.data?.message ||
            "";

          if (
            typeof errorMsg === "string" &&
            (errorMsg.toLowerCase().includes("already use") ||
              errorMsg.toLowerCase().includes("deja existent") ||
              errorMsg.toLowerCase().includes("folosit"))
          ) {
            toast.error(
              "Adresa de email este deja folosită! Utilizatorul nu a putut fi creat!"
            );
          } else {
            toast.error(
              "A intervenit o eroare. Utilizatorul nu a putut fi creat!"
            );
          }
        }
      });
    }
  };

  /**
   * Upload File Functions
   */

  const handleUserFileUpload = async (e) => {
    await uploadSingleFile(
      e,
      ({ message, blob }) => {
        if (message) {
          toast.error(t(message));
          return;
        }
        setUserFileBlob(blob);
      },
      "image"
    );
  };

  const handleCompanyFileUpload = async (e) => {
    await uploadSingleFile(
      e,
      ({ message, blob }) => {
        if (message) {
          toast.error(t(message));
          return;
        }
        setCompanyFileBlob(blob);
      },
      "image"
    );
  };

  /**
   * UseEffects
   */
  useEffect(() => {
    const keyDownHandler = (event) => {
      if (event.key === "Enter") {
        event.preventDefault();

        if (recoverPassword === true) {
          handleSubmitRecoverForm();
        } else if (showSignUp === true) {
          handleSubmitSignUpForm();
        } else {
          handleSubmitFormik();
        }
      }
    };

    document.addEventListener("keydown", keyDownHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [recoverPassword, showSignUp]);

  const getLocations = async () => {
    CountiesAPI.get()
      .then((res) => {
        if (res.ok) {
          setLocations(res.data.content);
        } else {
          throw new Error("Request failed");
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    getLocations();
  }, []);

  return (
    <>
      <Helmet>
        {/* <title>InBusiness Club Suceava :: {t("Login")}</title> */}
        <title>InBusiness Club Suceava :: Autentificare</title>
      </Helmet>
      <ThemeProvider theme={customTheme}>
        <Box
          sx={{
            width: "100vw",
            minHeight: "100vh",
            backgroundColor: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              position: "relative",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: "2.5rem",
            }}
          >
            <Box
              sx={{
                maxWidth: showSignUp ? "100%" : "500px",
                flexShrink: 0,
                borderRadius: "15px",
                padding: "25px 32px",
                boxShadow: "#eaeef4 0px 7px 29px 0px;",
              }}
              className={`${styles.boxWidth} ${
                showSignUp && styles.boxSignUpWidth
              }`}
            >
              <div className={styles.imageWrapper}>
                <img
                  style={{
                    width: "100%",
                    maxWidth: "400px",
                  }}
                  src={loginImage}
                  alt="InBusiness Logo"
                />
              </div>
              {!recoverPassword && !showSignUp ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Formik
                    innerRef={formRef}
                    initialValues={{
                      ...INITIAL_FORM_STATE,
                    }}
                    validationSchema={FORM_VALIDATION}
                    onSubmit={(values) => {
                      handleLogin(values);
                    }}
                  >
                    <Form>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "25px",
                        }}
                      >
                        <TextField name="email" label="Email" />
                        {/* <PasswordField name="password" label={t("Password")} /> */}
                        <PasswordField name="password" label={"Parola"} />
                      </div>
                    </Form>
                  </Formik>

                  <UtilityButton
                    onClick={handleSubmitFormik}
                    color="blue"
                    variant="contained"
                    style={{
                      padding: "12px 14px",
                    }}
                  >
                    {/* {t("Login")} */}
                    Autentifică-te
                  </UtilityButton>
                  <Divider />

                  <div
                    className={`${
                      styles.buttonsWrapper + " " + styles.buttonsWidthFull
                    }`}
                  >
                    <UtilityButton
                      onClick={() => setShowSignUp(true)}
                      color="blue"
                      style={{
                        padding: "12px 14px;",
                      }}
                    >
                      {/* {t("Sign Up")} */}
                      Înregistreză-te
                    </UtilityButton>

                    <UtilityButton
                      onClick={() => setRecoverPassword(true)}
                      color="blue"
                      style={{
                        padding: "12px 14px;",
                      }}
                    >
                      {/* {t("Recover password")} */}
                      Recuperează parola
                    </UtilityButton>
                  </div>
                </Box>
              ) : recoverPassword ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div>
                    <NavigationButton
                      onClick={() => setRecoverPassword(false)}
                      startIcon={<NavigateBeforeIcon />}
                    >
                      <Typography
                        sx={{ fontSize: "12px", color: "rgba(0, 0, 0, 0.62)" }}
                      >
                        {/* <div>{t("Back")}</div> */}
                        <div>Înapoi</div>
                      </Typography>
                    </NavigationButton>
                  </div>
                  <Typography variant="loginForm">
                    {/* {t(
                      "Enter the email address you created your account with. You will receive a password reset link at this address."
                    )} */}
                    Introduceți adresa de email pe care are ați creat contul
                    dumneavoastră. Pe această adresa veți primi un link de
                    resetare a parolei.
                  </Typography>

                  <Formik
                    innerRef={recoverFormRef}
                    initialValues={{
                      ...INITIAL_FORM_STATE_RECOVER,
                    }}
                    validationSchema={FORM_VALIDATION_RECOVER}
                    onSubmit={(values) => {
                      handleRecoverPassword(values);
                    }}
                  >
                    <Form>
                      <TextField name="email" label="Email" />
                    </Form>
                  </Formik>

                  <UtilityButton
                    onClick={handleSubmitRecoverForm}
                    color="blue"
                    variant="contained"
                    style={{
                      padding: "12px 14px",
                    }}
                  >
                    {/* {t("Send reset link")} */}
                    Trimitere link de resetare
                  </UtilityButton>
                </Box>
              ) : showSignUp ? (
                <React.Fragment>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    <Formik
                      innerRef={signUpFormRef}
                      initialValues={{ ...INITIAL_FORM_STATE_SIGNUP }}
                      validationSchema={FORM_VALIDATION_SIGNUP}
                      onSubmit={(values) => {
                        handleSignUp(values);
                      }}
                    >
                      {(props) => {
                        return (
                          <Form className={styles.formComponent}>
                            <div className={styles.formWrapper}>
                              <div className={styles.formContainer}>
                                <div className={styles.formColumn}>
                                  <div className={styles.columnContainer}>
                                    <TextField
                                      name="lastName"
                                      // label={`${t("Last Name")}*`}
                                      label="Nume*"
                                      size="medium"
                                    />
                                    <TextField
                                      name="firstName"
                                      // label={`${t("First Name")}*`}
                                      label="Prenume*"
                                      size="medium"
                                    />

                                    <PasswordField
                                      name="password"
                                      label={"Parolă*"}
                                    />
                                    <TextField
                                      name="phone"
                                      // label={`${t("Phone")}*`}
                                      label="Telefon*"
                                      size="medium"
                                      type="tel"
                                    />
                                    <TextField
                                      name="email"
                                      // label={`${t("Email")}*`}
                                      label="Email*"
                                      size="medium"
                                      type="email"
                                    />
                                    <Select
                                      name="countyId"
                                      size="medium"
                                      // label={`${t("Club location")}*`}
                                      label="Locația clubului*"
                                    >
                                      {locations.map((location) => {
                                        return (
                                          <MenuItem
                                            key={location.id}
                                            value={location.id}
                                          >
                                            {location.name}
                                          </MenuItem>
                                        );
                                      })}
                                    </Select>
                                    <TextField
                                      name="companyName"
                                      // label={`${t("Company")}*`}
                                      label="Companie*"
                                      size="medium"
                                    />
                                    <TextField
                                      name="companyDetails"
                                      // label={`${t("Site")}*`}
                                      label="Site*"
                                      size="medium"
                                    />
                                    {/* <Select
                                  name="type"
                                  label={"Status*"}
                                  size="medium"
                                >
                                  {statusList.map((status) => {
                                    return (
                                      <MenuItem
                                        key={status.id}
                                        value={status.id}
                                        sx
                                      >
                                        {status.name}
                                      </MenuItem>
                                    );
                                  })}
                                </Select> */}
                                    <div
                                      aria-labelledby="checkbox-group"
                                      className={
                                        inputStyles.checkboxGroupWrapper
                                      }
                                      onBlur={props.handleBlur}
                                      style={{ margin: "20px 0" }}
                                    >
                                      <div
                                        className={inputStyles.checkboxGroup}
                                      >
                                        <label>
                                          <Field type="checkbox" name="gdpr" />

                                          <a
                                            href="https://inbusiness.club/politica-de-confidentialitate"
                                            target={"_blank"}
                                            rel="noreferrer"
                                            className={inputStyles.gdpr}
                                          >
                                            Sunt de acord cu termenii și
                                            condițiile GDPR
                                          </a>
                                        </label>
                                      </div>
                                      {props.errors.gdpr &&
                                      props.touched.gdpr ? (
                                        <div
                                          className={
                                            inputStyles.checkboxGroupError
                                          }
                                        >
                                          {props.errors.gdpr}
                                        </div>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div className={styles.formColumn}>
                                  <div className={styles.columnContainer}>
                                    <div className={styles.imageWrapper}>
                                      <Typography className={styles.text}>
                                        {/* {t("Add profile picture")} */}
                                        Adaugă imagine de profil
                                      </Typography>
                                      <div
                                        className={styles.imageWrapperContainer}
                                      >
                                        <Avatar
                                          src={
                                            userFileBlob
                                              ? URL.createObjectURL(
                                                  userFileBlob
                                                )
                                              : DefaultUserPicture
                                          }
                                          className={styles.avatar}
                                        />
                                        <FileUploadWrapper
                                          onUpload={handleUserFileUpload}
                                        >
                                          <UtilityButton
                                            variant="contained"
                                            type="button"
                                            height="41px"
                                          >
                                            {/* {userFileBlob
                                          ? t("CHANGE PHOTO")
                                          : t("UPLOAD")} */}
                                            {userFileBlob
                                              ? "SCHIMBĂ FOTOGRAFIA"
                                              : "ÎNCARCĂ"}
                                          </UtilityButton>
                                        </FileUploadWrapper>
                                      </div>
                                    </div>
                                    <div className={styles.imageWrapper}>
                                      <Typography className={styles.text}>
                                        Adaugă logo-ul companiei
                                      </Typography>
                                      <div
                                        className={styles.imageWrapperContainer}
                                      >
                                        <Avatar
                                          src={
                                            companyFileBlob
                                              ? URL.createObjectURL(
                                                  companyFileBlob
                                                )
                                              : DefaultCompanyPicture
                                          }
                                          className={styles.avatar}
                                          variant="rounded"
                                        />
                                        <FileUploadWrapper
                                          onUpload={handleCompanyFileUpload}
                                        >
                                          <UtilityButton
                                            variant="contained"
                                            type="button"
                                            height="41px"
                                          >
                                            {companyFileBlob
                                              ? "SCHIMBĂ FOTOGRAFIA"
                                              : "ÎNCARCĂ"}
                                          </UtilityButton>
                                        </FileUploadWrapper>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Form>
                        );
                      }}
                    </Formik>
                    <Divider />
                    <div
                      className={`${styles.buttonsWrapper} ${styles.buttonsWidth40}`}
                    >
                      <UtilityButton
                        onClick={() => setShowSignUp(false)}
                        color="blue"
                        style={{
                          padding: "12px 14px;",
                        }}
                      >
                        {/* {t("Back to Login")} */}
                        Înapoi la autentificare
                      </UtilityButton>

                      <UtilityButton
                        onClick={handleSubmitSignUpForm}
                        color="blue"
                        variant="contained"
                        type="submit"
                        style={{
                          padding: "12px 14px",
                        }}
                      >
                        {/* {t("Sign Up")} */}
                        Înregistrează-te
                      </UtilityButton>
                    </div>
                  </Box>
                </React.Fragment>
              ) : (
                <React.Fragment>&nbsp;</React.Fragment>
              )}
              {/* <div className={styles.allRights}>
                <p>
                  &copy; <span>INBUSINESS CLUB. </span>
                  {t("All rights reserved")}
                </p>
              </div> */}
            </Box>
          </Box>

          <Box
            sx={{
              paddingBlock: "2.5rem",
              textAlign: "center",
            }}
          >
            <a
              href="https://raisissoftware.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#64748b",
                textDecoration: "none",
                display: "inline-block",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.color = "#2563eb";
                e.target.style.textDecoration = "underline";
              }}
              onMouseLeave={(e) => {
                e.target.style.color = "#64748b";
                e.target.style.textDecoration = "none";
              }}
            >
              Infrastructură software dezvoltată de RaisisSoftware
            </a>
          </Box>
        </Box>
      </ThemeProvider>
    </>
  );
}

export default Login;
